/*
 OTAExamplePage.tsx
 =========

------------------------------------------------------------------------
 Author:   Lucas Meier                                        2025-08-29
 Modifs:

 Goal:   Demonstrator for FUOTA status tracking & management for TRACE.

   (c) 2025 Lucas Meier
   --------------------------

   CSEM S.A.
   Jaquet-Droz 1
   CH-2000 Neuchâtel
   http://www.csem.ch


   THIS SOFTWARE IS CONFIDENTIAL AND CANNOT BE DISTRIBUTED
   WITHOUT THE CSEM PRIOR WRITTEN AGREEMENT.

   THIS  SOFTWARE CAN ONLY BE USED IN THE EUROPEAN PROJECT TRACE AND
   SHALL REMAIN PRIVATE AT ALL TIMES.

------------------------------------------------------------------------
*/

// OTAExamplePage.tsx — unified OTA bucket + unified Wisenode/ dropdown for all cards
import { Box } from '@mui/material';
import { mqtt5 } from "aws-iot-device-sdk-v2";
import { toUtf8 } from "@aws-sdk/util-utf8-browser";
import React, { useCallback, useEffect, useState } from "react";
// import { Col, Row, Button, ProgressBar } from "react-bootstrap";
import { useInjection } from "inversify-react";
import { MQTTClientProvider } from "../services/DependencyInjection";
import RangeSlider from "react-bootstrap-range-slider";
import styled from "styled-components";
import { Credentials } from "../services/Credentials";
import awsConfig from "../aws-config.json";
import awsResources from "../aws-resources.json";
// import { CopyObjectCommand, S3, _Object } from "@aws-sdk/client-s3";
import { IoTClient, DescribeJobExecutionCommand } from "@aws-sdk/client-iot";
import "./OTAExample.css";
import { Auth } from "aws-amplify";
import traceLogo from '../assets/3dtrace.png';
// S3 drag-and-drop imports

import {
    GetObjectCommand,
    CommonPrefix,
    PutObjectCommandOutput,
     CopyObjectCommand, S3, _Object 

} from "@aws-sdk/client-s3";
import { FileUploader } from "react-drag-drop-files";
import { Button, ListGroup, Spinner,  Dropdown, DropdownButton,  Col, Row, ProgressBar   } from "react-bootstrap";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

// =================== Reusable per-device OTA state ===================

type OtaJobState = {
  thingName: string;          // stored per job
  selectedItem: string;
  otaRunning: boolean;
  progress: number;
  progressDetails: string;
  jobID: string;
  currentVersion: string;
};

type OtaJob = {
  state: OtaJobState;
  setSelectedItem: (v: string) => void;
  setOtaRunning: (v: boolean) => void;
  setProgress: (v: number, details?: string) => void;
  setProgressDetails: (v: string) => void;
  setJobID: (v: string) => void;
  setCurrentVersion: (v: string) => void;
};

function useOtaJob(thingName: string, initial?: Partial<OtaJobState>): OtaJob {
  const [state, setState] = useState<OtaJobState>({
    thingName,
    selectedItem: initial?.selectedItem ?? "",
    otaRunning: initial?.otaRunning ?? false,
    progress: initial?.progress ?? 0,
    progressDetails: initial?.progressDetails ?? "",
    jobID: initial?.jobID ?? "",
    currentVersion: initial?.currentVersion ?? "",
  });

  return {
    state,
    setSelectedItem: (v) => setState((s) => ({ ...s, selectedItem: v })),
    setOtaRunning: (v) => setState((s) => ({ ...s, otaRunning: v })),
    setProgress: (v, details) =>
      setState((s) => ({ ...s, progress: v, ...(details !== undefined ? { progressDetails: details } : {}) })),
    setProgressDetails: (v) => setState((s) => ({ ...s, progressDetails: v })),
    setJobID: (v) => setState((s) => ({ ...s, jobID: v })),
    setCurrentVersion: (v) => setState((s) => ({ ...s, currentVersion: v })),
  };
}

// =================== Small presentational card (dropdown layout) ===================

type OtaCardProps = {
  title: string;
  titleDetail?: string;
  protocol: string;
  items: string[]; // dropdown list
  job: OtaJob;

  startLabel?: string;
  disabled?: boolean;
  onStart: (ctx: { selectedItem: string; job: OtaJob }) => Promise<void> | void;
  onRefreshVersion?: (job: OtaJob) => Promise<void> | void;
};

const OtaCard: React.FC<OtaCardProps> = ({
  title,
  titleDetail,
  protocol,
  items,
  job,
  startLabel = "Start OTA",
  disabled,
  onStart,
  onRefreshVersion,
}) => {
  const {
    state: { selectedItem, currentVersion, progress, progressDetails, otaRunning },
    setSelectedItem,
  } = job;

  const displayVersion = currentVersion?.replace(/_/g, "+");

  return (
    <div className="chart-col-inner">
      <h3 className="chart-title">{title.replace("-STM","")}</h3>
      {titleDetail ? <h4 className="chart-title">{titleDetail}</h4> : null}

      <div className="version-info">
        <p>Current Version: {displayVersion}</p>
        {/* <p>Comm Protocol: {protocol}</p> */}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <p style={{ margin: 0 }}>Select new version:</p>
          <StyledSelect
            value={selectedItem}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedItem(e.target.value)}
          >
            {items.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </StyledSelect>
        </div>

        <Button
          className="ota-button"
          onClick={() => onStart({ selectedItem, job })}
          disabled={!!disabled || otaRunning || !selectedItem}
        >
          {startLabel}
        </Button>

        <p>OTA details: {progressDetails}</p>
        <ProgressBar
          animated={progress > 0 && progress < 100}
          now={progress}
          label={`${Math.round(progress)}%`}
        />

        {onRefreshVersion && (
          <Button className="update-button" onClick={() => onRefreshVersion(job)}>
            Refresh version
          </Button>
        )}
      </div>
    </div>
  );
};

// =================== Styling ===================

// const themeColors = {
//   primary: "#00A1E5",
//   dark: "#003264",
//   secondary2: "#FFF",
//   primary1: "#00A1E5",
// };
// themeColors.ts
export const themeColors = {
  primary:   '#045722',   // TRACE primary (MUI primary.main)
  primary1:  '#16A34A',   // Focus/hover accent (TRACE "dark" accent)
  dark:      '#174704',   // Deep green for text/accents on light bg
  secondary2:'#FFFFFF',   // Surface (kept white for fields/panels)
  // (optional) handy extras if needed elsewhere
  // light:   '#1f8c07',  // TRACE primary.light
  // border:  'rgba(4, 87, 34, 0.25)',  // soft green border
};


export const StyledSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  padding: 10px 12px;
  border: 2px solid ${themeColors.primary};
  border-radius: 8px;
  background-color: ${themeColors.secondary2};
  color: ${themeColors.dark};
  font-size: 16px;
  line-height: 1.25;
  transition: border-color .2s ease, box-shadow .2s ease;

  &:hover {
    border-color: ${themeColors.primary1};
  }

  /* Green focus ring (replaces the default blue outline) */
  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${themeColors.primary1};
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.35); /* #16A34A @ 35% */
  }

  /* Hide the old IE arrow if present */
  &::-ms-expand {
    display: none;
  }

  /* Options keep the light surface and deep green text */
  option {
    background-color: ${themeColors.secondary2};
    color: ${themeColors.dark};
  }

  /* Disabled state (optional) */
  &:disabled {
    background-color: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
    opacity: 0.9;
  }
`;


export const DropArea = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  display: grid;
  place-items: center;
  text-align: center;

  padding: 18px 20px;
  min-height: 120px;

  color: ${themeColors.dark};
  background: ${themeColors.secondary2};
  border: 2px dashed ${themeColors.primary};
  border-radius: 12px;

  transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease;

  /* Hover & active drag */
  &:hover,
  ${({ $active }) => $active && '&'} {
    border-color: ${themeColors.primary1};
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
    background: #f3fbf5;
  }

  /* Disabled */
  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: .6;
    cursor: not-allowed;
    filter: grayscale(10%);
  `}

  /* Optional small subtitle styling */
  small {
    display: block;
    color: #366002;
    opacity: .9;
    margin-top: 6px;
  }
`;


// =================== Main Page ===================

type Props = {
  onLogout: () => void;
};
export default function OTAExamplePage({ onLogout }: Props) {
  // ---- named shadow constants ----
    const [dragging, setDragging] = React.useState(false);
  const PACKAGE_NAME = "ldm-TRACE-Wisenode-STM32"; // key inside the named shadow
  const JOB_END="$aws/events/jobExecution/";
  // ---- injection & clients ----
  const mqttClientProvider = useInjection<MQTTClientProvider>("MQTTClientProvider");
  const credentials = useInjection(Credentials);

  const IOT_GROUPS = ["Trace-Wisenode-13-STMs"]

  const [mqttClient, setMqttClient] = useState<mqtt5.Mqtt5Client | null>(null);
  const [s3Client, setS3Client] = useState<S3 | null>(null);
  const [iotClient, setIotClient] = useState<IoTClient | null>(null);

  // ---- OTA jobs (thing names are stored in the hook) ----

  const jobs = [useOtaJob("bike-3-STM"), useOtaJob("bike-4-STM"), useOtaJob("bike-5-STM"), useOtaJob("bike-6-STM")];

  // ---- S3 items: single unified list from Wisenode/ ----
  const [wisenodeItems, setWisenodeItems] = useState<string[]>([]);

  // buckets
  const source_bucket = awsResources.BackendBucketName as string;
  const ota_bucket = "ldm-trace-signswisenodes-s3triggersmcubootsignandf-q7kyvhqhre4l"; // <- unified OTA bucket

  // ---------- init AWS clients ----------
  useEffect(() => {
    const init = async () => {
      const creds = await credentials.get();
      const s3 = new S3({ region: awsConfig.aws_cognito_region, credentials: creds });
      const iot = new IoTClient({ region: awsConfig.aws_cognito_region, credentials: creds });
      setS3Client(s3);
      setIotClient(iot);
    };
    init().catch(console.error);
  }, [credentials]);

  // ---------- list S3 artifacts for dropdowns (simplified: only Wisenode/) ----------
  // const updateList = useCallback(async () => {
  //   if (!s3Client) return;
  //   const output = await s3Client.listObjectsV2({ Bucket: source_bucket, Prefix: "Wisenode/" });
  //   const res = output?.Contents ?? [];

  //   const items = (res as _Object[])
  //     .map((o) => o?.Key)
  //     .filter((k): k is string => !!k && k.startsWith("Wisenode/") && !k.endsWith("/")) // no "folders"
  //     .map((k) => k.replace("Wisenode/", ""));

  //   setWisenodeItems(items);

  //   // default selection: last item for each job (if available)
  //   const last = items.length > 0 ? items[items.length - 1] : "";
  //   if (last) {
  //     jobs.forEach((j) => j.setSelectedItem(last));
  //   }
  // }, [s3Client, source_bucket, jobs]);


  
  const updateList = useCallback(async () => {
    if (!s3Client) return;

    const output = await s3Client.listObjectsV2({ Bucket: source_bucket, Prefix: "Wisenode/" });
    const res = output?.Contents ?? [];
    const items = (res as _Object[])
      .map((o) => o?.Key)
      .filter((k): k is string => !!k && k.startsWith("Wisenode/") && !k.endsWith("/"))
      .map((k) => k.replace("Wisenode/", ""));

    setWisenodeItems(items);

    const last = items.at(-1) ?? "";

    // ✅ Only set a value if none is selected yet, or the current selection is invalid
    jobs.forEach((j) => {
      const cur = j.state.selectedItem;
      if ((!cur && last) || (cur && !items.includes(cur))) {
        j.setSelectedItem(last);
      }
    });
  }, [s3Client, source_bucket]); // ⬅️ remove `jobs` from deps


  useEffect(() => {
    updateList().catch(console.error);
  }, [updateList]);

  // ---------- MQTT connection & subscriptions ----------
  // Topics are derived from the jobs' thing names
  const SHADOWS_TOPIC = jobs.map((job) => `$aws/things/${job.state.thingName}/shadow/name/$package`);
  const JOB_NOTIFY_TOPICS = jobs.map((job) => `$aws/things/${job.state.thingName}/jobs/notify`);

    function indexSubstring(arr:Array<string>, substring:string) {
      return arr.findIndex(str => str.startsWith(substring.substring(0,str.length)));
    }
    function indexSubstringPostfix(arr:Array<string>, substring:string,postfix:string) {
      return arr.findIndex(str => (str+postfix) === (substring));
    }
    function indexSubstringPrefix(arr:Array<string>, substring:string,prefix:string) {
      return arr.findIndex(str => (prefix+str) === (substring));
    }
    useEffect(() => {
        const init = async function () {
            let client: mqtt5.Mqtt5Client = await mqttClientProvider();
            setMqttClient(client);

            await client.subscribe({
                subscriptions: [
                    ...JOB_NOTIFY_TOPICS.map(element => ({
                      qos: mqtt5.QoS.AtMostOnce, topicFilter:element
                    }))
                    // ,{qos: mqtt5.QoS.AtMostOnce, topicFilter: BLE_RX+"#"}
                ]
            });
            await client.subscribe({
              subscriptions: [
                  //$aws/things/thingName/jobs/jobId/update
                  ...SHADOWS_TOPIC.map(element => ({
                    qos: mqtt5.QoS.AtMostOnce, topicFilter:element+"/get/accepted"
                  }))
                  ,
                  {qos: mqtt5.QoS.AtMostOnce, topicFilter: JOB_END+"#"}
              ]
          });
            console.log("Subscriptionts are "+                [
                                          ...SHADOWS_TOPIC.map(element => (
                                            element+"/get/accepted"
                                          )),
                                          ...JOB_NOTIFY_TOPICS.map(element => (
                                            element
                                          )),
                                          JOB_END+"#"
                                          // ,BLE_RX+"#"
                                      ]);
            client.on("messageReceived", (eventData: mqtt5.MessageReceivedEvent): void => {
              // let i=indexSubstringPrefix(MAC_ADDRESSES,eventData.message.topicName,BLE_RX);
              // if(i>=0){
              //   if (eventData.message.payload) {
              //       }
              //       return;
              //   }  
                
                let i=indexSubstringPrefix(IOT_GROUPS,eventData.message.topicName,JOB_END);  // /+/update
                if(i>=0){
                      /**
                       * {
                              "eventType": "JOB_EXECUTION",
                              "eventId": "4fdfd0cf-a262-45ac-ba20-84d045f1c5e7",
                              "timestamp": 1732617378,
                              "operation": "failed",
                              "jobId": "ldm-nRF52840-lightweights-e7243646-83e4-ldm-s3-to-ota-lightweigh",
                              "thingArn": "arn:aws:iot:eu-west-1:481665093859:thing/ldm-nRF52-lightweight-0",
                              "status": "FAILED",
                              "statusDetails": {
                                  "currentProgress": "0. S3 download failed."
                              }
                          }
                        */
                if (eventData.message.payload) {
                  let job_update = JSON.parse(toUtf8(eventData.message.payload as Buffer));
                  console.log("Got an OTA information update!: "+JSON.stringify(job_update));
                  let failed = job_update.status === "FAILED";
                  let success=job_update.status === "SUCCEEDED";
                  let details = job_update.statusDetails.currentProgress;
                  jobs[i].setProgressDetails(details.toString());
                  if(failed){
                      // setJobIDList[i]("");
                      jobs[i].setJobID("");//notif.jobs.QUEUED[0].jobId);
                      jobs[i].setProgress(0);
                  }else if(success){
                      jobs[i].setJobID("");
                      jobs[i].setProgress(100);
                  }
                  else{
                      jobs[i].setProgress(Math.min(99,jobs[i].state.progress+1));
                  }
              }
                return;
            }                  
              i=indexSubstring(JOB_NOTIFY_TOPICS,eventData.message.topicName);
              if(i>=0){
                /**
                 * {
                    "timestamp": 1732633982,
                    "jobs": {
                        "QUEUED": [
                        {
                            "jobId": "ldm-nRF52840-lightweights-7cf5ce88-930b-ldm-s3-to-ota-lightweigh",
                            "queuedAt": 1732633981,
                            "lastUpdatedAt": 1732633981,
                            "executionNumber": 1,
                            "versionNumber": 1
                        }
                        ]
                    }
                    }
                  */
                if (eventData.message.payload) {
                  let job_notif = JSON.parse(toUtf8(eventData.message.payload as Buffer));
                  console.log("Got an OTA information update!: "+job_notif.toString());
                  if("QUEUED" in job_notif?.jobs){
                      console.log("Do I need another check here ? Setting job ID to:"+job_notif.jobs.QUEUED[0].jobId);
                      jobs[i].setJobID(job_notif.jobs.QUEUED[0].jobId);
                  }
              }
                return;
              }

            i = indexSubstringPostfix(SHADOWS_TOPIC,eventData.message.topicName,"/get/accepted");
            if(i>=0){
              let fw_ver = JSON.parse(toUtf8(eventData.message.payload as Buffer));
              // console.log("Got a shoadow information update!: "+fw_ver);
              // console.log("Got2 a shoadow information update!: "+eventData.message.payload);
              if(Object.keys(fw_ver?.state).length>0 && PACKAGE_NAME in fw_ver?.state?.reported && fw_ver?.state?.reported[PACKAGE_NAME]?.version) {
                jobs[i].setCurrentVersion(fw_ver.state.reported[PACKAGE_NAME].version)
                // setVersionsList[i](fw_ver.state.reported[PACKAGE_NAME].version)
              }
              return;
            }
            });
        };
        init().catch(err => console.log(err));
    }, [mqttClientProvider]);//, ...jobs.map((j) => j.state.thingName),]);

  // ---------- Refresh firmware version (publish /get) ----------
  const fetchFwVersion = useCallback(async () => {
    if (!mqttClient){
        // console.error("No mqttClient for FW fetching");
      return;
    } 
    // return;
    console.log("FETCHING NOW");
    for (const topic of SHADOWS_TOPIC) {
      await mqttClient.publish({
        qos: mqtt5.QoS.AtMostOnce,
        topicName: `${topic}/get`,
        payload: JSON.stringify({}),
      });
    }
  }, [mqttClient, SHADOWS_TOPIC]);


    const test = useCallback(async () => {
        console.error("No mqttClient for mqttClient mqttClient");
  }, [mqttClient]);

  useEffect(() => {
    let fetchVInterval : NodeJS.Timeout | undefined;
    fetchFwVersion();
    if (mqttClient) { 
        fetchVInterval=setInterval(() => { fetchFwVersion();     }, 5000);  
    }else{
      //TODO: wait for jobIDMqttBle before executing interval !
        clearInterval(fetchVInterval);
    }
        return () => clearInterval(fetchVInterval);
    },[mqttClient])

  // ---------- Poll job execution details for each active job ----------
  const fetchJobExecutionDetails = useCallback(
    async (
      jobID: string,
      thingName: string,
      setProgress: (p: number) => void,
      setProgressDetails: (s: string) => void,
      clearJobID: () => void,
    ) => {
      if (!jobID || !iotClient) return;
      try {
        const response = await iotClient.send(new DescribeJobExecutionCommand({ thingName, jobId: jobID }));
        const status = response?.execution?.status;

        if (status === "FAILED" || status === "CANCELED") {
          clearJobID();
          setProgress(0);
          setProgressDetails(`Job ended with state ${status}`);
          return;
        }
        if (status === "SUCCEEDED") {
          clearJobID();
          setProgress(100);
          return;
        }

        // heuristic progress
        const ver = (response?.execution?.versionNumber ?? 0) + 1;
        if (ver > 20) setProgress(Math.min(99, 85 + (ver - 15)));
        else if (ver > 15) setProgress(Math.min(99, 75 + (ver - 15) * 2));
        else setProgress(ver * 5);

        const detailsMap = response?.execution?.statusDetails?.detailsMap;
        if (detailsMap?.currentProgress) setProgressDetails(detailsMap.currentProgress);
        else if (detailsMap?.progress) setProgressDetails(detailsMap.progress);
        else if (status === "QUEUED") setProgressDetails("Waiting for device connection");
        else if (detailsMap) setProgressDetails(JSON.stringify(detailsMap));
      } catch (e) {
        console.error("Error fetching job execution details:", e);
      }
    },
    [iotClient],
  );

  // custom hook for polling a single job — uses the job's own thingName
  function useJobPolling(job: OtaJob) {
    useEffect(() => {
      let ref: ReturnType<typeof setInterval> | undefined;
      if (job.state.jobID) {
        ref = setInterval(() => {
          fetchJobExecutionDetails(
            job.state.jobID,
            job.state.thingName,
            (p) => job.setProgress(p),
            (s) => job.setProgressDetails(s),
            () => job.setJobID(""),
          );
        }, 1000);
      }
      return () => {
        if (ref) clearInterval(ref);
      };
    }, [job.state.jobID, job.state.thingName, fetchJobExecutionDetails]);
  }

  useJobPolling(jobs[0]);
  useJobPolling(jobs[1]);
  useJobPolling(jobs[2]);
  useJobPolling(jobs[3]);

  // ---------- S3 helper (copy selected item to unified OTA bucket) ----------
const moveFile = async (
  sourceBucket: string,
  destinationBucket: string,
  srcFileKey: string,
  dstFileKey: string
) => {
  if (!s3Client) return;

  const normalizedSrcKey = decodeURIComponent(srcFileKey);
  const normalizedDstKey = decodeURIComponent(dstFileKey);

  console.log(`Copying from ${sourceBucket}/${normalizedSrcKey} to ${destinationBucket}/${normalizedDstKey}`);

  // Optional: verify source exists
  try {
    await s3Client.headObject({ Bucket: sourceBucket, Key: normalizedSrcKey });
  } catch (err) {
    console.error(`Source key not found: ${normalizedSrcKey}`, err);
    throw new Error(`Source key does not exist: ${normalizedSrcKey}`);
  }

  await s3Client.send(
    new CopyObjectCommand({
      CopySource: `${sourceBucket}/${normalizedSrcKey}`,
      Bucket: destinationBucket,
      Key: normalizedDstKey,
    })
  );
};

  // ---------- UI state ----------
  const [rangeInSeconds, setRangeInSeconds] = useState<number>(20);

  // ---------- Card configs (all dropdown layout with the same items) ----------
type StartFn = (ctx: { selectedItem: string; job: OtaJob }) => Promise<void> | void;

type CardConfig = {
  title: string;
  titleDetail?: string;
  protocol: string;
  items: string[];
  job: OtaJob;
  start: StartFn;
};
  // const moveFileSecureURL = async (
  //   sourceBucket: string,
  //   destinationBucket: string,
  //   srcFileKey: string,
  //   dstFileKey: string
  // ) => {
  //   if (!s3Client) return;

  //   // Ensure CopySource uses raw key (decode if needed)
  //   const normalizedSrcKey = decodeURIComponent(srcFileKey);

  //   await s3Client.send(
  //     new CopyObjectCommand({
  //       CopySource: `${sourceBucket}/${normalizedSrcKey}`,
  //       Bucket: destinationBucket,
  //       Key: dstFileKey, // destination key can keep '+'
  //     })
  //   );
  // };
// Your default implementation (note the fix to avoid capturing jobs[0])
const defaultStart: StartFn = async ({ selectedItem, job }) => {
  if (!selectedItem) return;
  job.setOtaRunning(true);
  job.setProgress(1, "Signing FW & Launching OTA");
  try {
    await moveFile(source_bucket, ota_bucket, `Wisenode/${selectedItem}`,`Wisenode/${job.state.thingName}/${selectedItem}`);
  } finally {
    job.setOtaRunning(false);
  }
};

// Allow callers to omit `start`
type CardConfigInput = Omit<CardConfig, "start"> & Partial<Pick<CardConfig, "start">>;

function createCardConfig(input: CardConfigInput): CardConfig {
  return {
    start: defaultStart,
    ...input,
    // if caller provided a custom start, it will override the default
  };
}

// Usage
const cardConfigs = jobs.map(job =>
  createCardConfig({
    title: job.state.thingName,
    titleDetail: "",
    protocol: "WiFi",
    items: wisenodeItems,
    job,
    // start omitted -> defaultStart will be used
  })
);


// -------------------------------- S3 drag-and-drop utility ---------------------------------
// const credentials = useInjection(Credentials);
    // const [s3Client, setS3Client] = useState<S3 | null>(null);
    const [uploadInProgress, setUploadInProgress] = useState(false);
    const [loadInProgress, setLoadInProgress] = useState(false);
    const [selectedUploadItem, setSelectedUploadItem] = useState("Wisenode");
    const [uploadItems, setUploadItems] = useState<_Object[]>([]);

    const bucket = awsResources.BackendBucketName;
    
    const updateUploadList = useCallback(async () => {
        setLoadInProgress(true);
        const output = await s3Client?.listObjectsV2({ Bucket: bucket });
        setUploadItems(output?.Contents || []);
        setLoadInProgress(false);
        console.log(output);
    }, [s3Client]);

    useEffect(() => {
        updateUploadList();
    }, [s3Client, updateUploadList]);

    useEffect(() => {
        const init = async function () {

            var s3 = new S3({
                region: awsConfig.aws_cognito_region,
                credentials: await credentials.get()
            });
            setS3Client(s3);
        }

        init().catch(error => console.log(error));

    }, []);

        const PREFIX = 'Wisenode/';

        // Helper type guard: keep only items that actually have a string Key
        const hasKey = <T extends { Key?: string }>(i: T): i is T & { Key: string } =>
        typeof i.Key === 'string' && i.Key.length > 0;

        // Build the list to render:
        // 1) ensure Key exists
        // 2) keep only keys that start with "Wisenode/"
        // 3) hide S3 "directories" (trailing '/')
        const files = (uploadItems ?? [])
        .filter(hasKey)
        .filter(i => i.Key.startsWith(PREFIX))
        .filter(i => !i.Key.endsWith('/'));


    // const UploaderGreen: React.FC<{
    //   uploadInProgress: boolean;
    //   handleUpload: (files: FileList) => Promise<void>;
    // }> = ({ uploadInProgress, handleUpload }) => {
    



  return (
    
<>
    <div className="row align-items-center mb-3">
      <div className="col-1 d-flex">
        <button
          onClick={async () => {
            await Auth.signOut({ global: false });
            onLogout();
          }}
          className="btn btn-outline-primary btn-sm w-100"
          title="Sign out"
        >
          <i className="bi bi-box-arrow-right" />
        </button>
      </div>

      <div className="col">
        {/* Add 'text-end' if you want the text right-aligned */}
        <h1 className="title mb-0">Firmware Update over the Air Demos</h1>
      </div>
    </div>

    <div className="ota-example">
      <div className="text-center my-3">
        {(loadInProgress || uploadInProgress) && <Spinner animation="border" />}
      </div>

        <center>
        
      <FileUploader
        multiple
        label="Upload a new FW version (ends with VMaj.Min.Rev+Build.bin)"
        handleChange={async (files: FileList) => {
            setUploadInProgress(true);
            let promises: Promise<PutObjectCommandOutput>[] = [];
            Array.from(files).forEach(file => {
                const promise = s3Client?.putObject({
                    Bucket: bucket,
                    Key: selectedUploadItem+"/"+file.name,
                    Body: file
                });
                console.log("Uploaded key:", selectedUploadItem+"/"+file.name);
                if (promise)
                    promises.push(promise);
                else 
                    console.log("File " + file.name + " could not be uploaded...");
            });
            await Promise.all(promises);
            setUploadInProgress(false);
            updateUploadList();
            await updateList();
            }}
        name="file"
        onDraggingStateChange={(isDragging: boolean) => setDragging(isDragging)}
      >
        <DropArea $active={dragging} $disabled={uploadInProgress}>
          <div>
            <strong style={{ color: '#045722' }}>
              Drop a new FW version
            </strong>{' '}
            or click to select
            <small> (ends with VMaj.Min.Rev+Build.bin)</small>
          </div>
        </DropArea>
      </FileUploader>

        </center>
        <br/>

        <Row className="chart-row">
          {cardConfigs.map((cfg, idx) => (
            <Col key={idx} xs={12} md={3} className="chart-col">
              <OtaCard
                title={cfg.title}
                titleDetail={cfg.titleDetail}
                protocol={cfg.protocol}
                items={cfg.items}
                job={cfg.job}
                startLabel="Start OTA"
                disabled={!mqttClient}
                onStart={async ({ selectedItem, job }) => cfg.start({ selectedItem, job })}
                onRefreshVersion={() => fetchFwVersion()}
              />
            </Col>
          ))}
        </Row>
      </div>
      
<Row >
  <Col key={0} xs={12} md={3} />
  <Col key={1} xs={12} md={3} />
  <Col key={2} xs={12} md={3} />
  <Col key={3} xs={12} md={3} >
    <Box
      component="img"
      src={traceLogo}
      alt="TRACE logo"
      sx={{
        position: 'relative',
        right: { xs: 12, sm: 16 },
        bottom: { xs: 12, sm: 16 },
        width: { xs: 168, sm: 264, md: 330 },
        zIndex: (theme) => theme.zIndex.appBar - 1, // below AppBar/Drawer if they overlap
        pointerEvents: 'none',
        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.25))',
      }}
    />
</Col>
</Row>
    </>
  );
}